import {
  IdcardOutlined,
  DownloadOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Row,
  Col,
  Input,
  Table,
  Select,
  message,
  Spin,
} from "antd";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const { Option } = Select;
const { RangePicker } = DatePicker;

export default function FingerHeadOffice() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { authData, backUrl } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const isMobile = window.innerWidth < 768;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter options from backend
  const [organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  // Filters state → what the user is currently editing
  const [filters, setFilters] = useState({
    empCode: "",
    empName: "",
    organisation: "",
    department: "",
    designation: "",
    fromDate: null,
    toDate: null,
  });

  // Applied filters → what is actually used for querying
  const [appliedFilters, setAppliedFilters] = useState({
    empCode: "",
    empName: "",
    organisation: "",
    department: "",
    designation: "",
    fromDate: null,
    toDate: null,
  });

  // ────────────────────────────────────────────────
  // Fetch master data
  // ────────────────────────────────────────────────
  const fetchOrganisations = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setOrganisations(res.data.results || res.data || []);
    } catch (err) {
      console.error("Failed to fetch organisations:", err);
      message.error("Failed to fetch organisations");
    }
  };

  const fetchDepartments = async (orgId) => {
    if (!orgId) {
      setDepartments([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/departments/?organisation=${orgId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDepartments(res.data.results || res.data || []);
    } catch (err) {
      console.error("Failed to fetch departments:", err);
      message.error("Failed to fetch departments");
      setDepartments([]);
    }
  };

  const fetchDesignations = async (deptId) => {
    if (!deptId) {
      setDesignations([]);
      return;
    }
    try {
      const res = await axios.get(
        `${backUrl}/api/emp/designations/?department=${deptId}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setDesignations(res.data.results || res.data || []);
    } catch (err) {
      console.error("Failed to fetch designations:", err);
      message.error("Failed to fetch designations");
      setDesignations([]);
    }
  };

  // ────────────────────────────────────────────────
  // Core data fetch function
  // ────────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
      };

      if (appliedFilters.empCode) params.emp_code = appliedFilters.empCode;
      if (appliedFilters.empName) params.Emp_Name = appliedFilters.empName;
      if (appliedFilters.organisation) params.organisation = appliedFilters.organisation;
      if (appliedFilters.department) params.department = appliedFilters.department;
      if (appliedFilters.designation) params.designation = appliedFilters.designation;
      if (appliedFilters.fromDate) params.from_date = appliedFilters.fromDate;
      if (appliedFilters.toDate) params.to_date = appliedFilters.toDate;

      const res = await axios.get(`${backUrl}/api/hr/headofficefingerprint/`, {
        headers: {
          Authorization: `Bearer ${authData.access}`,
        },
        params,
      });

      const records = res.data.results || res.data || [];
      const count = res.data.count || records.length;

      const recordsWithKeys = records.map((record, index) => ({
        ...record,
        key: record.id || `${record.Emp_Id}-${record.Date}-${record.Time}-${index}`,
      }));

      setData(recordsWithKeys);
      setTotalRecords(count);
    } catch (err) {
      console.error("Error fetching fingerprint data:", err);
      message.error("Failed to load fingerprint data");
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchOrganisations();
    // fetchData() will be called by the next useEffect on mount
  }, [authData?.access, backUrl]);

  // React to applied filters + pagination changes
  useEffect(() => {
    fetchData();
  }, [appliedFilters, currentPage, pageSize]);

  // Cascade fetch departments & designations when applied values change
  useEffect(() => {
    if (appliedFilters.organisation) {
      fetchDepartments(appliedFilters.organisation);
    } else {
      setDepartments([]);
    }
  }, [appliedFilters.organisation]);

  useEffect(() => {
    if (appliedFilters.department) {
      fetchDesignations(appliedFilters.department);
    } else {
      setDesignations([]);
    }
  }, [appliedFilters.department]);

  // ────────────────────────────────────────────────
  // Filter handlers
  // ────────────────────────────────────────────────
  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };

      if (key === "organisation") {
        newFilters.department = "";
        newFilters.designation = "";
      } else if (key === "department") {
        newFilters.designation = "";
      }

      return newFilters;
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1); // reset to first page on new filter apply
  };

  const handleClearFilters = () => {
    const cleared = {
      empCode: "",
      empName: "",
      organisation: "",
      department: "",
      designation: "",
      fromDate: null,
      toDate: null,
    };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setCurrentPage(1);
  };

  // ────────────────────────────────────────────────
  // Excel Export
  // ────────────────────────────────────────────────
  const handleExportExcel = async () => {
    try {
      message.loading("Preparing export...", 0);

      if (selectedRowKeys.length > 0) {
        const rowsToExport = data.filter((row) => selectedRowKeys.includes(row.key));
        exportToExcel(rowsToExport);
        return;
      }

      // Export all matching records (no pagination)
      const params = {};
      if (appliedFilters.empCode) params.emp_code = appliedFilters.empCode;
      if (appliedFilters.empName) params.Emp_Name = appliedFilters.empName;
      if (appliedFilters.organisation) params.organisation = appliedFilters.organisation;
      if (appliedFilters.department) params.department = appliedFilters.department;
      if (appliedFilters.designation) params.designation = appliedFilters.designation;
      if (appliedFilters.fromDate) params.from_date = appliedFilters.fromDate;
      if (appliedFilters.toDate) params.to_date = appliedFilters.toDate;

      const res = await axios.get(`${backUrl}/api/hr/headofficefingerprint/`, {
        headers: { Authorization: `Bearer ${authData.access}` },
        params,
      });

      const allRecords = res.data.results || res.data || [];
      message.destroy();
      exportToExcel(allRecords);
    } catch (err) {
      message.destroy();
      console.error("Error exporting data:", err);
      message.error("Failed to export data");
    }
  };

  const exportToExcel = (records) => {
    if (!records.length) {
      message.warning("No records to export");
      return;
    }

    const formattedData = records.map((item) => ({
      "Employee Code": item.Emp_Id || "",
      "Employee Name": item.Emp_Name || "",
      Date: item.Date || "",
      "Punch-In Time": item.Time || "",
      "Device Name": item.Device_Name || "",
      "Device ID": item.Device_Id || "",
      Company: item?.employee_details?.organisation || "",
      Department: item?.employee_details?.department || "",
      Designation: item?.employee_details?.designation || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "HeadOfficeFingerprint");

    XLSX.writeFile(workbook, "HeadOffice_Fingerprint.xlsx");
    message.success(`Exported ${records.length} records successfully`);
  };

  // ────────────────────────────────────────────────
  // Table Columns
  // ────────────────────────────────────────────────
  const columns = [
    { title: "Employee Code", dataIndex: "Emp_Id", key: "Emp_Id" },
    { title: "Employee Name", dataIndex: "Emp_Name", key: "Emp_Name" },
    { title: "Date", dataIndex: "Date", key: "Date" },
    { title: "Punch-In Time", dataIndex: "Time", key: "Time" },
    { title: "Device Name", dataIndex: "Device_Name", key: "Device_Name" },
    { title: "Device ID", dataIndex: "Device_Id", key: "Device_Id" },
    {
      title: "Company",
      dataIndex: ["employee_details", "organisation"],
      key: "Company",
    },
    {
      title: "Department",
      dataIndex: ["employee_details", "department"],
      key: "Department",
    },
    {
      title: "Designation",
      dataIndex: ["employee_details", "designation"],
      key: "Designation",
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    setSelectedRowKeys([]); // clear selection on page change
  };

  return (
    <>
      <section>
        {isMobile ? (
          <center>
            <p
              style={{
                fontSize: "15px",
                textTransform: "uppercase",
                color: "#0e497b",
              }}
            >
              <IdcardOutlined /> Head Office Fingerprint Records
            </p>
          </center>
        ) : (
          <center>
            <h5>
              <IdcardOutlined /> Head Office Fingerprint Records
            </h5>
          </center>
        )}

        <Card
          style={{
            margin: "20px",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <label>Employee Code</label>
              <Input
                placeholder="Enter Employee Code"
                value={filters.empCode}
                onChange={(e) => handleFilterChange("empCode", e.target.value)}
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <label>Employee Name</label>
              <Input
                placeholder="Enter Employee Name"
                value={filters.empName}
                onChange={(e) => handleFilterChange("empName", e.target.value)}
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <label>Company Name</label>
              <Select
                placeholder="Select Company"
                style={{ width: "100%" }}
                allowClear
                value={filters.organisation || undefined}
                onChange={(value) => handleFilterChange("organisation", value ?? "")}
              >
                {organisations.map((org) => (
                  <Option key={org.id} value={org.id}>
                    {org.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <label>Department</label>
              <Select
                showSearch
                placeholder="Select Department"
                style={{ width: "100%" }}
                allowClear
                value={filters.department || undefined}
                onChange={(value) => handleFilterChange("department", value ?? "")}
                filterOption={(input, option) =>
                  (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                }
                disabled={!filters.organisation}
              >
                {departments.map((dept) => (
                  <Option key={dept.id} value={dept.id}>
                    {dept.name}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
            <Col xs={24} sm={12} md={9} lg={6}>
              <label>Designation</label>
              <Select
                showSearch
                placeholder="Select Designation"
                style={{ width: "100%" }}
                allowClear
                value={filters.designation || undefined}
                onChange={(value) => handleFilterChange("designation", value ?? "")}
                filterOption={(input, option) =>
                  (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                }
                disabled={!filters.department}
              >
                {designations.map((des) => (
                  <Option key={des.id} value={des.id}>
                    {des.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <label>Date Range</label>
              <RangePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                value={
                  filters.fromDate && filters.toDate
                    ? [dayjs(filters.fromDate, "YYYY-MM-DD"), dayjs(filters.toDate, "YYYY-MM-DD")]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates.length === 2) {
                    const fromDate = dates[0].format("YYYY-MM-DD");
                    const toDate = dates[1].format("YYYY-MM-DD");
                    handleFilterChange("fromDate", fromDate);
                    handleFilterChange("toDate", toDate);
                  } else {
                    handleFilterChange("fromDate", null);
                    handleFilterChange("toDate", null);
                  }
                }}
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                style={{ marginRight: 8, backgroundColor: "#0e497b", borderColor: "#0e497b", marginTop: 20 }}
              >
                Apply Filters
              </Button>
              <Button onClick={handleClearFilters}>Clear Filters</Button>
            </Col>
          </Row>

          {/* <Row justify="end" >
            
          </Row> */}
        </Card>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "15px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <Button
            icon={<DownloadOutlined />}
            style={{
              height: "36px",
              width: "180px",
              backgroundColor: "#0e497b",
              color: "white",
            }}
            onClick={handleExportExcel}
            disabled={loading}
          >
            Download {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : "All"}
          </Button>

          {selectedRowKeys.length > 0 && (
            <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
          )}
        </div>

        <Spin spinning={loading} tip="Loading fingerprint records...">
          <Table
            style={{ marginTop: "10px" }}
            columns={columns}
            dataSource={data}
            bordered
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRecords,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
              pageSizeOptions: ["10", "20", "50", "100", "200"],
            }}
            onChange={handleTableChange}
            scroll={{ x: "max-content" }}
            rowSelection={rowSelection}
            rowKey="key"
            components={{
              header: {
                cell: (props) => (
                  <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                    {props.children}
                  </th>
                ),
              },
            }}
          />
        </Spin>
      </section>
    </>
  );
}